import { Capacitor } from "@capacitor/core";
import { spErrorTags } from "@eventr/common";
import { ColoursObj } from "@eventr/common-storybook";
import { NativeNavigation } from "@eventr/native/navigation";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStatusBarTone } from "feat/appState/statusBarTone";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useScrollContainerRef } from "state/scrollState/scrollSignals";
import { useTopInset } from "state/windowState/window.store";

/**
 * How much of the carousel header is off the top of the screen, in px. Read by the sticky
 * section headers that park below it, so they follow it up and rest against the top of the
 * screen once it has gone.
 */
const HIDDEN_VAR = "--home-header-hidden";

/**
 * The tab bar's share of its slide travel gone, 0 (in view) to 1 (off screen). The controls
 * fixed over the bar (the Home FAB, "See past entries") scale their bottom inset by `1 - this`.
 */
const FOOTER_GONE_VAR = "--home-tabbar-gone-ratio";

/**
 * iOS keeps the tab bar outside the web view, so the page cannot measure it. It slides off
 * over this much scroll, about a docked bar and the home indicator below it, which keeps it
 * close to 1:1 with the finger; the plugin works out the real distance itself.
 */
const NATIVE_TAB_BAR_TRAVEL = 83;

/**
 * The header fades out over its last stretch of travel, measured in status bars: what is
 * left of it by then sits under the status bar, where sliding the rest of the way out from
 * behind the clock reads as a seam. Fading it hands the page over instead.
 */
const FADE_IN_STATUS_BARS = 2;

interface HomeScrollAway {
  /** Registers the header that slides off the top. */
  setHeader: (el: HTMLDivElement | null) => void;
  /** Registers the tab bar that slides off the bottom; iOS drives its own without one. */
  setFooter: (el: HTMLDivElement | null) => void;
  /** Registers a scroller of the view's own; without one the page's scroll container drives it. */
  setScroller: (el: HTMLElement | null) => void;
  /** Registers the wrapper the page's content sits in. */
  setContent: (el: HTMLDivElement | null) => void;
  /** Whether the view reaches up behind the header rather than starting below it. */
  behindHeader: boolean;
  setBehindHeader: (behind: boolean) => void;
}

const HomeScrollAwayContext = createContext<HomeScrollAway | null>(null);

const NO_OP: HomeScrollAway = {
  setHeader: () => {},
  setFooter: () => {},
  setScroller: () => {},
  setContent: () => {},
  behindHeader: false,
  setBehindHeader: () => {},
};

/** Falls back to no-ops outside the provider (tests, storybook) so consumers never crash. */
export function useHomeScrollAway(): HomeScrollAway {
  return useContext(HomeScrollAwayContext) ?? NO_OP;
}

function setNativeTabBarOffset(offset: number) {
  NativeNavigation.setTabBarOffset({ offset }).catch((err) => {
    spErrorTags(`Failed to slide tab bar to ${offset}`, ["NativeNavigation"], err);
  });
}

/**
 * Moves the itinerary carousel off the top of the screen and the tab bar off the bottom as
 * the home page scrolls down, and brings both back as it scrolls up. Each follows the
 * scroll delta 1:1 and stops at its own height, so the shorter tab bar is gone first and
 * back first. iOS slides its native tab bar the same way through the plugin, and brings it
 * back in full whenever this view leaves the screen for another tab or a pushed page.
 *
 * Both are fixed overlays the page travels underneath, so nothing here lays the page out
 * again: a view that scrolls inside itself has to reach up behind the header for that (see
 * HomeItinerary), rather than start below it.
 */
export function HomeScrollAwayProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const scrollContainerRef = useScrollContainerRef();
  const { addViewWillDisappearListener } = useNativeNavigationViewContext();
  const topInset = useTopInset();
  const [header, setHeader] = useState<HTMLDivElement | null>(null);
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const [behindHeader, setBehindHeader] = useState(false);

  const api = useMemo(
    () => ({ setHeader, setFooter, setScroller, setContent, behindHeader, setBehindHeader }),
    [behindHeader],
  );

  useEffect(() => {
    const scrolled = scroller ?? scrollContainerRef.current;
    const nativeTabBar = isIosNativeNavigation();

    /** How far each of them is off the screen, in px. */
    let headerGone = 0;
    let footerGone = 0;
    /** The native tab bar's share of the same travel, and the offset last sent for it. */
    let nativeGone = 0;
    let nativeOffset = 0;

    const restore = () => {
      if (header) {
        header.style.transform = "";
        header.style.opacity = "";
        header.style.pointerEvents = "";
      }
      if (footer) footer.style.transform = "";
      content?.style.removeProperty(HIDDEN_VAR);
      document.documentElement.style.removeProperty(FOOTER_GONE_VAR);
      useStatusBarTone.getState().setHeaderUnderBar(true);
    };

    restore();
    if (!enabled || !scrolled || (!header && !footer && !nativeTabBar)) return;

    let headerHeight = header?.offsetHeight ?? 0;
    let footerHeight = footer?.offsetHeight ?? 0;
    let lastTop = Math.max(scrolled.scrollTop, 0);
    let frame = 0;

    // Mounting part way down a list the user has already scrolled starts them hidden. A
    // scroller with nothing to scroll cannot bring them back, so there they start in view.
    if (scrolled.scrollHeight > scrolled.clientHeight) {
      headerGone = Math.min(lastTop, headerHeight);
      footerGone = Math.min(lastTop, footerHeight);
      nativeGone = Math.min(lastTop, NATIVE_TAB_BAR_TRAVEL);
    }

    /** The last stretch of the header's travel, over which it fades out. */
    const fadeOver = topInset * FADE_IN_STATUS_BARS;

    // Written straight to the nodes rather than through state: this runs on every scroll
    // frame, and a render per frame would re-render every slide in the carousel.
    const draw = () => {
      if (header) {
        header.style.transform = headerGone === 0 ? "" : `translate3d(0, ${-headerGone}px, 0)`;
        const left = headerHeight - headerGone;
        const opacity = fadeOver > 0 ? Math.min(left / fadeOver, 1) : 1;
        header.style.opacity = opacity === 1 ? "" : `${opacity}`;
        header.style.pointerEvents = opacity === 0 ? "none" : "";
        // Past half faded, the page's strip shows through more than the picture does, so
        // the status bar picks its text against that instead (see ItineraryCarousel).
        useStatusBarTone.getState().setHeaderUnderBar(opacity > 0.5);
      }
      if (footer) footer.style.transform = footerGone === 0 ? "" : `translate3d(0, ${footerGone}px, 0)`;
      content?.style.setProperty(HIDDEN_VAR, `${headerGone}px`);
      const barRatio = nativeTabBar
        ? nativeGone / NATIVE_TAB_BAR_TRAVEL
        : footerHeight > 0
          ? footerGone / footerHeight
          : 0;
      document.documentElement.style.setProperty(FOOTER_GONE_VAR, barRatio.toFixed(3));

      if (!nativeTabBar) return;
      // Whole pixels of travel: finer steps than that cost bridge calls without showing.
      const offset = Math.round(nativeGone) / NATIVE_TAB_BAR_TRAVEL;
      if (offset !== nativeOffset) {
        nativeOffset = offset;
        setNativeTabBarOffset(offset);
      }
    };

    /** Overscroll (rubber banding, pull to refresh) reports a top of 0, where they belong in view. */
    const advance = (gone: number, delta: number, limit: number, top: number) =>
      top === 0 ? 0 : Math.min(Math.max(gone + delta, 0), limit);

    const onFrame = () => {
      frame = 0;
      const raw = scrolled.scrollTop;
      const maxTop = Math.max(scrolled.scrollHeight - scrolled.clientHeight, 0);
      // Pulling past the bottom is not a scroll up: hold the bars through the rubber band's
      // settle back to rest.
      if (raw > maxTop) {
        lastTop = maxTop;
        return;
      }
      const top = Math.max(raw, 0);
      const delta = top - lastTop;
      lastTop = top;
      headerGone = advance(headerGone, delta, headerHeight, top);
      footerGone = advance(footerGone, delta, footerHeight, top);
      nativeGone = advance(nativeGone, delta, NATIVE_TAB_BAR_TRAVEL, top);
      draw();
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(onFrame);
    };

    // Slide cards expand and collapse and the tab bar collapses for the keyboard, so the
    // distance each has to travel changes while mounted.
    const observer = new ResizeObserver(() => {
      headerHeight = header?.offsetHeight ?? 0;
      footerHeight = footer?.offsetHeight ?? 0;
      headerGone = Math.min(headerGone, headerHeight);
      footerGone = Math.min(footerGone, footerHeight);
      draw();
    });

    if (header) observer.observe(header);
    if (footer) observer.observe(footer);
    scrolled.addEventListener("scroll", onScroll, { passive: true });
    draw();

    // Switching tab or pushing a page keeps this view mounted, scrolled where it was, while
    // another takes the screen. The plugin brings the bar back for it; the next scroll here
    // starts from the bar in view rather than jumping back to where it was left.
    const removeDisappearListener = addViewWillDisappearListener(() => {
      nativeGone = 0;
      nativeOffset = 0;
      // Redraw so the controls that follow the bar reset with it.
      draw();
    });

    return () => {
      removeDisappearListener();
      observer.disconnect();
      scrolled.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
      restore();
      // Leaving the page part way through the slide would strand the user without a tab bar.
      if (nativeOffset !== 0) setNativeTabBarOffset(0);
    };
  }, [enabled, header, footer, scroller, content, topInset, scrollContainerRef, addViewWillDisappearListener]);

  return <HomeScrollAwayContext.Provider value={api}>{children}</HomeScrollAwayContext.Provider>;
}

/**
 * The wrapper holding the page clear of the fixed header, and the scope of HIDDEN_VAR. A
 * view that reaches up behind the header takes the offset itself, where its own scroller
 * can carry the content under the header instead of stopping short of it.
 */
export function HomeScrollAwayContent({ headerOffset, children }: { headerOffset: number; children: ReactNode }) {
  const { setContent, behindHeader } = useHomeScrollAway();
  const topInset = useTopInset();
  return (
    <div
      ref={setContent}
      style={{ height: "100%", boxSizing: "border-box", paddingTop: behindHeader ? 0 : headerOffset }}
    >
      {/* The page scrolls up behind the status bar on iOS. Once the header has faded off it,
          this strip in the page colour is what the bar sits on, rather than the page's
          content passing under the clock. The header covers it while it is in view. */}
      {Capacitor.getPlatform() === "ios" && headerOffset > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: topInset,
            backgroundColor: ColoursObj.backgrounds.primary,
            zIndex: STATUS_BAR_STRIP_Z,
          }}
        />
      )}
      {children}
    </div>
  );
}

/** Above the page's sticky headers and overlays, below the carousel header (100). */
const STATUS_BAR_STRIP_Z = 50;

/** Where a sticky section header stops at the top of the screen: below the status bar on iOS. */
const stickyFloor = (topInset: number) => (Capacitor.getPlatform() === "ios" ? topInset : 0);

/**
 * How far a view that reaches up behind the header starts its own scroller below the top
 * of the screen. On iOS that is the status bar, so the list's pinned section header (which
 * a virtualised list pins to its own scroller's top) comes to rest below the bar.
 */
export function useHomeStatusBarClearance(): number {
  const topInset = useTopInset();
  const { behindHeader } = useHomeScrollAway();
  return behindHeader ? stickyFloor(topInset) : 0;
}

/**
 * Where a sticky section header parks: against the bottom edge of the header, wherever it is,
 * and no higher than the bottom of the status bar on iOS.
 */
export const homeHeaderStickyOffset = (offset: number, topInset: number) =>
  `max(${stickyFloor(topInset)}px, calc(${offset}px - var(${HIDDEN_VAR}, 0px)))`;
